<template>
  <main name="backup-settings-main-page">
    <b-card no-body>
      <!-- ? Header Section Start -->
      <section
        name="backup-setting__header"
        class="card-padding-top card-padding-x d-flex justify-content-between align-items-center"
      >
        <span class="card__header--topic d-flex d-md-none">Backup Command</span>
        <span class="card__header--topic d-none d-md-flex"
          >Backup Command Template List</span
        >
        <br v-if="hasUpdatePermission" class="d-block d-md-none" />
        <atom-button
          v-if="hasUpdatePermission"
          buttonId="create-new-backup-config"
          buttonStyle="border"
          @click="onClickCreateNewBackupConfig"
        >
          <div class="d-flex d-md-none">Create</div>
          <div class="d-none d-md-flex">Create New Backup Config</div>
        </atom-button>
      </section>
      <!-- ? Header Section End -->

      <!-- ? Filter Section Start -->
      <div class="row card-padding">
        <div class="col-12 col-sm-6">
          <atom-input
            v-model="filterList.nameOrDescription"
            title="Filter"
            placeholder="Backup Config Name or Description"
          ></atom-input>
        </div>
        <div
          class="col-12 col-sm-4 d-flex justify-content-end align-items-end justify-content-sm-start mt-2"
        >
          <atom-button
            buttonId="button-search"
            iconLeft="SearchIcon"
            @click="onClickButtonSearch"
            class="d-flex justify-content-end"
          ></atom-button>
          <div class="reset-filter-button ml-1" @click="clickButtonClearFilter">
            <span>Reset</span>
          </div>
        </div>
      </div>
      <!-- ? Filter Section End -->

      <!-- * Table Start -->
      <div class="row">
        <div class="col-12">
          <b-table
            sticky-header
            responsive
            :items="items"
            :fields="hasUpdatePermission ? fields : fields.filter(field => field.key !== 'deleteIcon')"
            :hover="true"
            @row-clicked="selectRow"
          >
            <template #cell(autoBackupConfigName)="data">
              <div class="header--text-bold">
                {{ data.value }}
              </div>
            </template>
            <template #cell(autoBackupConfigDescription)="data">
              <span>{{ data.value }}</span>
            </template>
            <template #cell(deleteIcon)="data">
              <atom-button
                buttonId="button-delete"
                iconLeft="Trash2Icon"
                buttonStyle="flat"
                buttonColor="secondary"
                :isDisable="false"
                @click="onClickDeleteAutoBackupConfig(data)"
              ></atom-button>
            </template>
          </b-table>
          <div class="row card-padding">
            <div class="col-6">Showing {{ filterList.totalCount }} entries</div>
            <div class="col-6 d-flex justify-content-end">
              <b-pagination
                v-model="filterList.currentPage"
                :total-rows="filterList.total"
                :per-page="perPage"
                size="sm"
                class="my-0"
              ></b-pagination>
            </div>
          </div>
        </div>
      </div>
      <!-- * Table End -->
    </b-card>
    <!-- ? Modal Delete Section Start -->
    <molecule-modal-dialog
      modalId="modal-confirm-delete-auto-backup-config"
      icon="AlertCircleIcon"
      :isTextCenter="true"
      :textTitle="`Do you want to delete\n${targetAutoConfigBackup.autoBackupConfigName}?`"
      textSubtitle="You won’t be able to revert this!"
      buttonTextLeft="Delete"
      buttonTextRight="Cancel"
      buttonColorLeft="danger"
      @clickButtonLeft="onClickConfirmDeleteFromModal"
    >
    </molecule-modal-dialog>
    <!-- ? Modal Delete Section End -->
  </main>
</template>

<script>
import { BCard, BTable, BPagination } from "bootstrap-vue"
import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"
import { PermissionUser } from "@/constants/permission-user"
import mixinsUser from "@/mixins/mixinsUser"

export default {
  name: "OrganismBackupSettings",
  components: {
    AtomButton,
    AtomInput,
    BCard,
    BTable,
    BPagination,
    MoleculeModalDialog,
  },
  data() {
    return {
      perPage: 10,
      targetAutoConfigBackup: {
        autoBackupConfigId: "",
        autoBackupConfigName: "",
        autoBackupConfigDescription: "",
      },
      modalId: null,
    }
  },
  mixins: [mixinsUser],
  props: {
    filterList: {
      required: true,
      type: Object,
      default: () => {},
    },
    fields: {
      type: Array,
      required: false,
      default: () => [],
    },
    items: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  computed: {
    hasUpdatePermission() {
      return this.hasPermission(PermissionUser.NCM.BACKUP_SETTINGS_WRITE)
    },
  },
  watch: {
    "filterList.currentPage": {
      deep: true,
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$emit("onUpdateCurrentPage", this.filterList)
        }
      },
    },
  },
  methods: {
    navigateTo(path) {
      this.$router.push(path)
    },

    onClickCreateNewBackupConfig() {
      this.navigateTo(
        "/network-configuration/backup-settings/create-new-commands"
      )
    },

    onClickButtonSearch() {
      this.$emit("onUpdateFilterData", this.filterList)
    },

    clickButtonClearFilter() {
      this.$emit("onSetDefaultFilterData")
    },

    selectRow(row) {
      const autoBackupConfigId = row.autoBackupConfigId
      this.$emit("clickRow", autoBackupConfigId)
    },

    closeModal() {
      this.$bvModal.hide(this.modalId)
      this.modalId = ""
    },

    setTargetAutoBackupConfigToDefault() {
      this.targetAutoConfigBackup = {
        autoBackupConfigId: "",
        autoBackupConfigName: "",
        autoBackupConfigDescription: "",
      }
    },

    onClickDeleteAutoBackupConfig(row) {
      this.targetAutoConfigBackup = row.item
      this.modalId = "modal-confirm-delete-auto-backup-config"
      this.$bvModal.show(this.modalId)
    },

    onClickConfirmDeleteFromModal() {
      const autoBackupConfigId = this.targetAutoConfigBackup.autoBackupConfigId
      this.$emit("onRequestDeleteAutoBackupConfig", autoBackupConfigId)
      this.closeModal()
      this.setTargetAutoBackupConfigToDefault()
    },
  },
}
</script>

<style lang="scss" scoped>
.card-padding {
  padding: 21px;
}

.card-padding-x {
  padding-left: 21px;
  padding-right: 21px;
}

.card-padding-y {
  padding-top: 21px;
  padding-bottom: 21px;
}

.card-padding-top {
  padding-top: 21px;
}

.card-padding-bottom {
  padding-bottom: 21px;
}

.card__header--topic {
  font-weight: 500;
  font-size: 18px;
}

.header--text-bold {
  font-weight: 600;
  font-size: 14px;
  color: #6e6b7b;
}

.reset-filter-button {
  color: #B9B9C3;
  margin-bottom: 8px;
  text-decoration: underline;
  &:hover {
    cursor: pointer;
    color: #7367F0;
  }
}
</style>
